/**
 * Indeterminate loading spinner. Token-driven, `role="status"` with an
 * accessible label (no bare literal — the caller supplies it), and honors
 * `prefers-reduced-motion` (the ring stops spinning).
 */
import type { JSX } from 'solid-js';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';

export type SpinnerSize = 'sm' | 'md' | 'lg';

const SIZE_VAR: Record<SpinnerSize, string> = {
  sm: 'var(--space-4)',
  md: 'var(--space-6)',
  lg: 'var(--space-8)',
};

const STYLE_ID = 'f4-spinner';
const CSS = `
.f4-spinner {
  display: inline-block;
  border: 2px solid var(--color-border);
  border-top-color: var(--color-accent);
  border-radius: var(--radius-circle);
  animation: f4-spin var(--duration-loop-fast, 800ms) var(--ease-linear, linear) infinite;
}
@media (prefers-reduced-motion: reduce) {
  .f4-spinner { animation: none; }
}
`;

export interface SpinnerProps {
  /** Accessible status text (e.g. "Loading"). */
  readonly label: string;
  /** Size preset. Defaults to `md`. */
  readonly size?: SpinnerSize;
  readonly class?: string;
}

export function Spinner(props: SpinnerProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);
  const dimension = (): string => SIZE_VAR[props.size ?? 'md'];
  return (
    <span
      class={cx('f4-spinner', props.class)}
      role="status"
      aria-label={props.label}
      style={{ width: dimension(), height: dimension() }}
    />
  );
}
